'use client';

import './gallery-cinema.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@sola/ark-core/utils/helpers.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';
import { getHeaderDisplayLevel } from '@sola/utils/assembly.js';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselLazyLoadImage,
  CarouselNext,
  CarouselPrevious,
} from '../../../molecules/carousel/carousel';
import { FeatureCard } from '../../../molecules/feature-card/feature-card';
import { HeaderGroup } from '../../../molecules/header-group/header-group';
import {
  Lightbox,
  LightboxTrigger,
} from '../../../molecules/lightbox/lightbox';
import { getPlaceholderItems } from '../../../utils/storybook';
import { getBaseSizeProps } from '../../../utils/text-utils';
import { JumbotronNotable } from '../../jumbotron/notable/jumbotron-notable';

export const GalleryCinema: React.FC<FlexibleContentProps> = ({
  seoLevel = 2,
  media,
  items,
  alt,
  ...props
}) => {
  const { t } = useTranslation(['core']);

  const hasHeaderContent = !!props.title || !!props.body;
  const hasHeaderCard = hasHeaderContent && media?.image;
  const hasHeaderGroup = hasHeaderContent && !media?.image;
  const headerDisplayLevel = getHeaderDisplayLevel(seoLevel);

  const galleryItems =
    !items || items.length < 1 ? getPlaceholderItems(items, 1) : items;

  const hasBodyItems = galleryItems.some((item) => !!item.body);

  return (
    <section
      className={cn('GalleryCinema component', { 'GalleryCinema--alt': alt })}
    >
      <div className="GalleryCinema__container container flex flex-col gap-comfy-xl lg:gap-roomy-xs">
        {hasHeaderCard && (
          <FeatureCard
            {...props}
            {...getBaseSizeProps(headerDisplayLevel)}
            seoLevel={seoLevel}
            media={media}
            imagePlacement="right"
            variant="horizontal"
            inline
          />
        )}
        {hasHeaderGroup && <HeaderGroup {...props} seoLevel={seoLevel} />}

        <Carousel
          aria-label={t('core:gallery.title', {
            title: props.title,
          })}
          opts={{
            align: 'start',
            loop: false,
            watchFocus: false,
          }}
        >
          <div className="GalleryCinema__wrapper flex flex-col gap-comfy-lg">
            <div
              className={cn('GalleryCinema__carousel', {
                'has-body': hasBodyItems,
              })}
              data-length={galleryItems.length}
            >
              <CarouselContent>
                {galleryItems.map((slide, index) => (
                  <CarouselItem
                    key={['slide', index, slide.title].join('-')}
                    index={index}
                    className="GalleryCinema__slide"
                    aria-label={`${t('core:gallery.slide.title', {
                      index: index + 1,
                      length: galleryItems.length,
                    })}${slide.title && ` - ${slide.title}`}`}
                  >
                    <figure className="m-none">
                      <Lightbox
                        key={['lightbox', index, slide.title].join('-')}
                        items={galleryItems}
                        startIndex={index}
                      >
                        <LightboxTrigger
                          className="Lightbox__trigger  w-full"
                          ariaLabel={t('core:gallery.open')}
                        >
                          <>
                            {!hasBodyItems && (
                              <div className="GalleryCinema__image">
                                <CarouselLazyLoadImage
                                  index={index}
                                  media={slide.media}
                                  isVideoThumbnail={!!slide.media?.video}
                                />
                              </div>
                            )}

                            {hasBodyItems && (
                              <JumbotronNotable
                                seoLevel={
                                  hasHeaderContent ? seoLevel + 1 : seoLevel
                                }
                                {...{ ...slide, alt, inline: true }}
                              />
                            )}
                          </>
                        </LightboxTrigger>
                      </Lightbox>

                      {slide.media?.caption && (
                        <figcaption className="GalleryCinema__attribution label label-lg mt-comfy-sm">
                          {slide.media.caption || slide.title}
                        </figcaption>
                      )}
                    </figure>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </div>
            {galleryItems.length > 1 && (
              <div className="GalleryCinema__arrows flex gap-comfy-sm items-center justify-end">
                <CarouselPrevious />
                <CarouselNext />
              </div>
            )}
          </div>
        </Carousel>
      </div>
    </section>
  );
};
